
import { PartnerSales } from '../page';

interface PartnerRankingProps {
  partners: PartnerSales[];
  onBack: () => void;
  onViewPartner: (partner: PartnerSales) => void;
  timeFilter: 'week' | 'month' | 'year';
  onTimeFilterChange: (filter: 'week' | 'month' | 'year') => void;
}

export default function PartnerRanking({ partners, onBack, onViewPartner, timeFilter, onTimeFilterChange }: PartnerRankingProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const getTimeFilterLabel = () => {
    switch (timeFilter) {
      case 'week': return 'tuần này';
      case 'month': return 'tháng này';
      case 'year': return 'năm này';
    }
  };

  const getRevenueByTimeFilter = (partner: PartnerSales) => {
    switch (timeFilter) {
      case 'week': return partner.thisWeekRevenue;
      case 'month': return partner.thisMonthRevenue;
      case 'year': return partner.thisYearRevenue;
    }
  };

  const getRankingIcon = (rank: number) => {
    switch (rank) {
      case 1: return 'ri-trophy-fill text-yellow-500';
      case 2: return 'ri-medal-fill text-gray-400';
      case 3: return 'ri-medal-fill text-orange-500';
      default: return 'ri-award-line text-blue-500';
    }
  };

  const getRankingBadge = (rank: number) => {
    switch (rank) {
      case 1: return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white';
      case 2: return 'bg-gradient-to-r from-gray-300 to-gray-400 text-white';
      case 3: return 'bg-gradient-to-r from-orange-400 to-orange-500 text-white';
      default: return 'bg-gradient-to-r from-blue-400 to-blue-500 text-white';
    }
  };

  // Sắp xếp partners theo doanh thu của thời gian được chọn
  const rankedPartners = [...partners]
    .filter(partner => partner.isActive) // Chỉ hiển thị partner đang hoạt động
    .sort((a, b) => getRevenueByTimeFilter(b) - getRevenueByTimeFilter(a))
    .map((partner, index) => ({
      ...partner,
      rank: index + 1,
      currentRevenue: getRevenueByTimeFilter(partner),
      currentCommission: getRevenueByTimeFilter(partner) * partner.commissionRate / 100
    }));

  const topPerformer = rankedPartners[0];
  const totalRevenue = rankedPartners.reduce((sum, partner) => sum + partner.currentRevenue, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700 p-2"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bảng xếp hạng Partner</h1>
            <p className="text-gray-600 mt-1">Vinh danh những Partner xuất sắc nhất</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeFilter}
            onChange={(e) => onTimeFilterChange(e.target.value as 'week' | 'month' | 'year')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
          >
            <option value="week">Tuần này</option>
            <option value="month">Tháng này</option>
            <option value="year">Năm này</option>
          </select>
        </div>
      </div>

      {/* Top Performer Highlight */}
      {topPerformer && (
        <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 rounded-xl p-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-trophy-fill text-4xl text-white"></i>
                </div>
                <div>
                  <h2 className="text-3xl font-bold">🏆 Partner xuất sắc nhất {getTimeFilterLabel()}</h2>
                  <p className="text-xl font-semibold mt-2">{topPerformer.partnerName}</p>
                  <p className="text-yellow-100 mt-1">{topPerformer.email}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-yellow-100 text-sm">Doanh thu {getTimeFilterLabel()}</p>
                <p className="text-4xl font-bold">{formatCurrency(topPerformer.currentRevenue)}</p>
                <p className="text-yellow-100 text-lg mt-1">
                  Hoa hồng: {formatCurrency(topPerformer.currentCommission)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ranking Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-team-line text-xl text-blue-600"></i>
            </div>
            <div>
              <p className="text-sm text-gray-500">Partner tham gia</p>
              <p className="text-2xl font-bold text-gray-900">{rankedPartners.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xl text-green-600"></i>
            </div>
            <div>
              <p className="text-sm text-gray-500">Tổng doanh thu</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-bar-chart-line text-xl text-purple-600"></i>
            </div>
            <div>
              <p className="text-sm text-gray-500">Trung bình/Partner</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(rankedPartners.length > 0 ? totalRevenue / rankedPartners.length : 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-hand-coin-line text-xl text-orange-600"></i>
            </div>
            <div>
              <p className="text-sm text-gray-500">Tổng hoa hồng</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(rankedPartners.reduce((sum, p) => sum + p.currentCommission, 0))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ranking Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Bảng xếp hạng chi tiết - {getTimeFilterLabel()}
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hạng
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Partner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Doanh thu
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hoa hồng
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % Đóng góp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Đơn hàng
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rankedPartners.map((partner) => {
                const contributionPercent = totalRevenue > 0 ? (partner.currentRevenue / totalRevenue * 100) : 0;
                
                return (
                  <tr key={partner.partnerId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRankingBadge(partner.rank)}`}>
                          <span className="font-bold text-sm">#{partner.rank}</span>
                        </div>
                        <i className={`text-2xl ${getRankingIcon(partner.rank)}`}></i>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {partner.partnerName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {partner.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(partner.currentRevenue)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-600">
                        {formatCurrency(partner.currentCommission)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {partner.commissionRate}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${Math.min(contributionPercent, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">
                          {contributionPercent.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {partner.totalOrders}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => onViewPartner(partner)}
                        className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1 rounded-lg transition-colors whitespace-nowrap"
                      >
                        <i className="ri-eye-line mr-1"></i>
                        Chi tiết
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {rankedPartners.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-trophy-line text-4xl text-gray-400 mb-4"></i>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có dữ liệu xếp hạng</h3>
            <p className="text-gray-500">Không có Partner nào hoạt động trong {getTimeFilterLabel()}</p>
          </div>
        )}
      </div>

      {/* Achievement Badges */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Huy hiệu thành tích
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 border border-yellow-300 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <i className="ri-trophy-fill text-3xl text-yellow-600"></i>
              <div>
                <h4 className="font-semibold text-yellow-800">Vua doanh số</h4>
                <p className="text-sm text-yellow-700">Doanh thu cao nhất {getTimeFilterLabel()}</p>
                <p className="text-sm font-medium text-yellow-900">
                  {topPerformer ? topPerformer.partnerName : 'Chưa có'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-100 to-green-200 border border-green-300 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <i className="ri-rocket-line text-3xl text-green-600"></i>
              <div>
                <h4 className="font-semibold text-green-800">Tăng trưởng mạnh</h4>
                <p className="text-sm text-green-700">Partner có xu hướng tăng trưởng tốt</p>
                <p className="text-sm font-medium text-green-900">
                  {rankedPartners.find(p => p.thisWeekRevenue > p.thisMonthRevenue / 4)?.partnerName || 'Chưa có'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-100 to-blue-200 border border-blue-300 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <i className="ri-heart-fill text-3xl text-blue-600"></i>
              <div>
                <h4 className="font-semibold text-blue-800">Khách hàng yêu thích</h4>
                <p className="text-sm text-blue-700">Nhiều đơn hàng nhất</p>
                <p className="text-sm font-medium text-blue-900">
                  {rankedPartners.sort((a, b) => b.totalOrders - a.totalOrders)[0]?.partnerName || 'Chưa có'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
